import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Spinner from '../layout/Spinner';
import { logout } from '../../actions/auth';
import { Redirect } from 'react-router-dom';
import EventoElement from '../eventi/EventoElement';
import { extractRoomKey, isDateTimeTodayAndPassed } from '../../utils/utilityfunction';
import AudioElement from '../audio/AudioElement';
import axios from 'axios';

const Agenda = ({ auth: { user, loading }, alert, logout }) => {

  const [eventiData, setEventiData] = useState([]);
  const [eventiLoading, setEventiLoading] = useState(true);
  const [audiosData, setAudiosData] = useState([]);
  const [, setRenderTick] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const eventTimerRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchEvents = () => {
    axios.get('/api/agenda/getEventiAgenda')
      .then(res => {
        if (mountedRef.current) {
          setEventiData(res.data);
          setEventiLoading(false);
        }
      })
      .catch(() => { if (mountedRef.current) setEventiLoading(false); });
  };

  // Timer client-side: scatta all'orario esatto del prossimo evento di oggi.
  // Usa il fuso orario locale del browser (italiano), quindi è sempre corretto.
  useEffect(() => {
    clearTimeout(eventTimerRef.current);
    const adesso = new Date();
    const oggiStr = adesso.toDateString();
    let minMs = null;
    eventiData.forEach(el => {
      const data = new Date(el.data);
      if (data.toDateString() !== oggiStr) return;
      const [ore, minuti] = el.orario.split(':').map(Number);
      data.setHours(ore, minuti, 0, 0);
      const ms = data.getTime() - adesso.getTime();
      if (ms > 0 && (minMs === null || ms < minMs)) minMs = ms;
    });
    if (minMs !== null) {
      eventTimerRef.current = setTimeout(() => setRenderTick(n => n + 1), minMs + 1000);
    }
    return () => clearTimeout(eventTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventiData]);

  // Controlla ogni 30s se un evento è diventato accessibile (nessuna chiamata API, solo re-render)
  useEffect(() => {
    const interval = setInterval(() => {
      if (mountedRef.current) setRenderTick(n => n + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Audio: caricato una volta sola
  useEffect(() => {
    axios.get('/api/audio/getAudios')
      .then(res => { if (mountedRef.current) setAudiosData(res.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchEvents();

    const connect = () => {
      if (!mountedRef.current) return;

      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${wsProtocol}//${window.location.host}?type=agenda`);
      wsRef.current = ws;

      ws.onopen = () => {
        // Ping ogni 30s per mantenere la connessione attiva su Heroku
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'agenda_updated') {
            fetchEvents();
          }
        } catch (e) {}
      };

      ws.onclose = () => {
        clearInterval(pingIntervalRef.current);
        if (mountedRef.current) {
          reconnectTimerRef.current = setTimeout(connect, 5000);
        }
      };
    };

    connect();

    return () => {
      mountedRef.current = false;
      clearTimeout(reconnectTimerRef.current);
      clearTimeout(eventTimerRef.current);
      clearInterval(pingIntervalRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Spinner />;

  if (user && user.ruolo === 'admin') {
    return <Redirect to="/gestione-portale" />;
  }

  return (
    <section className="fd-section wide">
      <div className="w-layout-blockcontainer fd-container w-container">
        <div className="fd-wrap">
          <div className="header">
            <span className="brand w-nav-brand">
              <img src="/assets/media/fondazione_danesino_logo.svg" sizes="(max-width: 479px) 170px, (max-width: 767px) 33vw, 170px" srcset="" alt="" className="image logo logo-fondazione" data-savepage-loading="lazy" />
              <img src="/assets/media/noi_con_voi-fondazione_danesino-logo--oriz.png" sizes="(max-width: 479px) 170px, (max-width: 767px) 33vw, 170px" srcset="" alt="" className="image logo" data-savepage-loading="lazy" />
            </span>
            <div className='nome'>
              <div>Ciao <strong className='nome-utente'>{user && user.username} </strong>
                {user && user.ruolo == 'admin' ? <a href="" className="link" onClick={() => logout()}>Clicca qui per uscire</a> : ''}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-layout-blockcontainer fd-container w-container">
        <div className="fd-wrap">
          <div className="w100 view-noiconvoi-anz">
            <div className="w50">
              <div>
                <h1>La tua agenda</h1>
                <p>I prossimi eventi organizzati</p>
              </div>
              <div className="call-elenco">
                {eventiLoading ? <Spinner /> : eventiData.length === 0 ?
                      <div className="nessun_evento">Non c'è nessun evento in programma, ci sentiamo presto!</div>
                      :
                  eventiData.map((el) => (
                    <EventoElement
                      key={el._id}
                      titoloEvento={el.titolo}
                      descrizioneEvento={el.descrizione}
                      dataEvento={el.data}
                      orarioEvento={el.orario}
                      linkEvento={el.link}
                      roomName={el.roomName}
                      roomKey={extractRoomKey(el.hostRoomUrl)}
                      archivio={false}
                      isActive={isDateTimeTodayAndPassed(el.data, el.orario, user)}
                    />
                  ))}
              </div>
            </div>
            <div className="w50 audio-block">
              <div>
                <h2>Materiale sempre a disposizione</h2>
                <p>Questo materiale sarà sempre a disposizione per te:</p>
              </div>
              <div className="call-elenco audio-archivio">
                {audiosData.map((el) => (
                  <AudioElement
                    key={el._id}
                    titoloAudio={el.titolo}
                    codiceAudio={el.codice}
                    descrizioneAudio={el.descrizione}
                    idAudio={el._id}
                    link={el.link}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
};

Agenda.propTypes = {
  auth: PropTypes.object.isRequired,
  alert: PropTypes.object.isRequired,
  logout: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  auth: state.auth,
  alert: state.alert,
});

export default connect(mapStateToProps, { logout })(Agenda);
